import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import loadingSpinnerData from '../assets/Loading spinner.json';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  ButtonGroup,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  CircularProgress,
  Tooltip,
  Stack,
  Alert,
  LinearProgress,
  Snackbar,
  Slide
} from '@mui/material';
import { ArrowUpward as ArrowUpwardIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Visibility as ViewIcon,
  Description as InvoiceIcon,
  FilterList as FilterIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon,
  FileDownload as ExportIcon,
  DateRange as DateRangeIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { customerService } from '../utils/apiService';
import ConfirmationModal from '../components/print/ConfirmationModal';
import ExportButton from '../components/ExportButton';
import * as XLSX from 'xlsx';



// Module-level cache to preserve table state across navigation
let cachedPage = 0;
let cachedRowsPerPage = 10;
let cachedSearchQuery = '';
let cachedActiveTab = 0;
let cachedFilters = {
  status: {
    active: true,
    inactive: true,
  },
  type: {
    B2B: true,
    B2C: true,
  }
};

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(cachedPage);
  const [rowsPerPage, setRowsPerPage] = useState(cachedRowsPerPage);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(cachedSearchQuery);
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState(cachedActiveTab);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState(cachedFilters);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // New import/export state
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [importFailedRecords, setImportFailedRecords] = useState([]); // New state for failed records
  const [parsedRows, setParsedRows] = useState([]);   // SheetJS parsed data
  const [parsedHeaders, setParsedHeaders] = useState([]);  // column headers

  // Field mapping state
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [bulkFields, setBulkFields] = useState([]);      // API fields
  const [bulkFieldsLoading, setBulkFieldsLoading] = useState(false);
  const [bulkFieldsError, setBulkFieldsError] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});  // { apiField: excelHeader | '' }

  // Toast / Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const exportColumns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'vatRegistrationNumber', headerName: 'VAT Number', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'address.street', headerName: 'Street', width: 200 },
    { field: 'address.city', headerName: 'City', width: 150 },
    { field: 'address.state', headerName: 'State', width: 150 },
    { field: 'address.postalCode', headerName: 'Postal Code', width: 120 },
    { field: 'address.country', headerName: 'Country', width: 150 },
    { field: 'customerType', headerName: 'Type', width: 100 },
    { field: 'isActive', headerName: 'Status', width: 100 }
  ];

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      let currentCustomerType = '';
      if (activeTab === 1) currentCustomerType = 'B2B';
      if (activeTab === 2) currentCustomerType = 'B2C';

      const response = await customerService.getCustomers(page + 1, rowsPerPage, searchQuery, currentCustomerType);
      console.log('Customers API response:', response); // Debug log

      if (Array.isArray(response)) {
        setCustomers(response);
        setTotalCount(response.length);
      } else if (response?.success && Array.isArray(response.data)) {
        setCustomers(response.data);
        setTotalCount(
          response.pagination?.total ||
          response.total ||
          response.data.length
        );
      } else {
        console.error('Invalid response format:', response);
        setError('Invalid response format from server');
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError(error.message || 'Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImportSample = async () => {
    try {
      setLoading(true);
      const response = await customerService.downloadBulkSample();

      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers_sample.xlsx');
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setError(null);
    } catch (err) {
      console.error('Error downloading sample:', err);
      setError(err.message || 'Failed to download sample file');
    } finally {
      setLoading(false);
    }
  };

  const buildDefaultMapping = (apiFields, excelHeaders) => {
    const mapping = {};
    apiFields.forEach((field) => {
      const fieldName = typeof field === 'object' ? (field.key || field.name || field.field || '') : field;
      const match = excelHeaders.find(
        (h) => h.toLowerCase().replace(/[\s_-]/g, '') === fieldName.toLowerCase().replace(/[\s_-]/g, '')
      );
      mapping[fieldName] = match || '';
    });
    return mapping;
  };

  const openMappingModal = async (file, headers, rows) => {
    setMappingModalOpen(true);
    setBulkFieldsLoading(true);
    setBulkFieldsError(null);
    try {
      const data = await customerService.getBulkFields();
      let fields = [];
      if (Array.isArray(data)) {
        fields = data;
      } else if (data && Array.isArray(data.fields)) {
        fields = data.fields;
      } else if (data && typeof data === 'object') {
        fields = Object.keys(data);
      }
      setBulkFields(fields);
      setFieldMapping(buildDefaultMapping(fields, headers));
    } catch (err) {
      console.error('Error fetching bulk fields:', err);
      setBulkFieldsError('Could not load API fields. You can still proceed with the raw data.');
      setBulkFields(headers);
      const fallback = {};
      headers.forEach((h) => { fallback[h] = h; });
      setFieldMapping(fallback);
    } finally {
      setBulkFieldsLoading(false);
    }
  };

  const handleImportExcel = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportError(null);
      setImportSuccess(null);
      setImportFailedRecords([]);
      setParsedRows([]);
      setParsedHeaders([]);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          if (rawRows.length === 0) {
            setImportError('The file is empty or has no data rows.');
            setMappingModalOpen(true);
            return;
          }
          const headers = Object.keys(rawRows[0]);
          setParsedHeaders(headers);
          setParsedRows(rawRows);
          openMappingModal(file, headers, rawRows);
        } catch (err) {
          console.error('SheetJS parse error:', err);
          setImportError('Could not parse the file. Make sure it is a valid Excel or CSV file.');
          setMappingModalOpen(true);
        }
      };
      reader.onerror = () => {
        setImportError('Failed to read the file.');
        setMappingModalOpen(true);
      };
      reader.readAsArrayBuffer(file);
    }
    event.target.value = '';
  };

  const handleCloseMappingModal = () => {
    if (!importLoading) {
      setMappingModalOpen(false);
    }
  };

  const handleMappingChange = (apiField, excelHeader) => {
    setFieldMapping((prev) => ({ ...prev, [apiField]: excelHeader }));
  };

  const handleConfirmImport = async () => {
    if (importLoading) return;
    if (!importFile || !parsedRows.length) return;
    setImportLoading(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      console.log('--- BULK UPLOAD INVESTIGATION ---');
      console.log('1. Parsed Excel Headers:', parsedHeaders);
      console.log('2. First Parsed Row (Before Mapping):', parsedRows[0]);
      console.log('3. Current Field Mapping:', fieldMapping);

      // Create transformed mapping and rows
      const cleanedMapping = {};
      const validationErrors = [];
      const transformedRows = [];

      for (let i = 0; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        const newRow = {};

        Object.entries(fieldMapping).forEach(([apiField, excelHeader]) => {
          if (excelHeader && excelHeader.trim() !== '') {
            cleanedMapping[apiField] = apiField;

            let val = row[excelHeader];
            // Handle and clean values
            if (val !== undefined && val !== null) {
              if (typeof val === 'string') {
                val = val.trim();
              }
              // Only assign if it's not empty after trimming
              if (val !== '') {
                if (apiField === 'customerType') {
                  val = String(val).toUpperCase();
                }
                newRow[apiField] = val;
              }
            }
          }
        });

        // Validation for mandatory fields
        const rowNum = i + 2; // Excel row number (assuming header is row 1)
        if (!newRow.name) {
          validationErrors.push(`Row ${rowNum}: "name" is required but is missing or empty.`);
        }

        if (!newRow.customerType || !['B2B', 'B2C'].includes(newRow.customerType)) {
          validationErrors.push(`Row ${rowNum}: "customerType" must be exactly B2B or B2C. Found: "${newRow.customerType || 'empty'}"`);
        }

        transformedRows.push(newRow);
      }

      if (Object.keys(cleanedMapping).length === 0) {
        throw new Error('Please map at least one field before importing.');
      }

      if (validationErrors.length > 0) {
        // Show the first few errors to the user clearly
        const errorMsg = `Validation failed for ${validationErrors.length} row(s):\n` +
          validationErrors.slice(0, 3).join('\n') +
          (validationErrors.length > 3 ? `\n...and ${validationErrors.length - 3} more errors.` : '');
        throw new Error(errorMsg);
      }

      // Pass cleanedMapping directly since customerService.bulkImport wraps it in fieldMap
      const exactHeaders = Object.keys(cleanedMapping);
      const worksheet = XLSX.utils.json_to_sheet(transformedRows, { header: exactHeaders });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      const newFile = new File([excelBuffer], importFile.name, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const result = await customerService.bulkImport(newFile, cleanedMapping);

      const importedCount =
        result?.imported ??
        result?.insertedCount ??
        result?.count ??
        transformedRows.length;
      const failedCount = result?.failed ?? result?.failedCount ?? 0;
      const failedRecords = result?.failedRecords || result?.errors || [];

      const successMsg =
        failedCount > 0
          ? `Imported ${importedCount} customer(s) — ${failedCount} skipped/failed. Check your data.`
          : `Successfully Created.`;

      if (failedRecords.length > 0 || failedCount > 0) {
        setImportError(`Import completed with ${failedCount} failed records.`);
        if (failedRecords.length > 0) {
          setImportFailedRecords(failedRecords);
        }
        showSnackbar(successMsg, 'warning');
        // Refresh to show any successfully imported records
        fetchCustomers();
      } else {
        setMappingModalOpen(false);
        setParsedRows([]);
        setParsedHeaders([]);
        setFieldMapping({});
        setImportFile(null);
        setImportError(null);
        setImportSuccess(null);
        setImportFailedRecords([]);
        showSnackbar(successMsg, 'success');
        fetchCustomers();
      }
    } catch (err) {
      console.error('Error importing excel:', err);
      setImportError(
        err?.message || 'Import failed. Please check the file and field mapping, then try again.'
      );
      if (err.failedRecords) {
        setImportFailedRecords(err.failedRecords);
      }
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage, searchQuery, activeTab]);

  const handleDeleteClick = (customer) => {
    setSelectedCustomer(customer);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedCustomer) {
        await customerService.deleteCustomer(selectedCustomer._id);
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
    } finally {
      setDeleteModalOpen(false);
      setSelectedCustomer(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleEditClick = (customer) => {
    if (customer && customer._id) {
      navigate(`/customers/edit/${customer._id}`);
    } else {
      console.error('Cannot edit customer: Invalid customer ID', customer);
      // Optionally show an error message to the user
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    cachedSearchQuery = value;
    setPage(0);
    cachedPage = 0;
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        [category]: {
          ...prev[category],
          ...value,
        },
      };
      cachedFilters = updated;
      return updated;
    });
  };

  const filteredCustomers = customers.filter(customer => {
    if (!customer) return false;

    // Tab-based filter
    const matchesTab =
      activeTab === 0 || // All Customers tab
      (activeTab === 1 && customer.customerType === 'B2B') || // B2B Customers tab
      (activeTab === 2 && customer.customerType === 'B2C'); // B2C Customers tab

    // Status filter
    const matchesStatus = filters.status.active && customer.isActive || filters.status.inactive && !customer.isActive;

    // Type filter
    const matchesType = filters.type.B2B && customer.customerType === 'B2B' || filters.type.B2C && customer.customerType === 'B2C';

    return matchesTab && matchesStatus && matchesType;
  });

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Customers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            View all your customers information
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant="outlined"

            size="small"
            onClick={handleImportSample}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            <DownloadIcon />

          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            size="small"
            component="label"
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            Import
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleImportExcel}
            />
          </Button>

          <ExportButton
            data={filteredCustomers}
            columns={exportColumns}
            filename="customers_export"
          />


          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => navigate('/customers/new')}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
              background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
              },
            }}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(event, newValue) => {
          setActiveTab(newValue);
          cachedActiveTab = newValue;
          setPage(0);
          cachedPage = 0;
        }}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label="All Customers" />
        <Tab label="B2B Customers" />
        <Tab label="B2C Customers" />
      </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search for customers..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: 500,
              height: 28,
              '& .MuiOutlinedInput-root': {
                borderRadius: '2px',
                height: 28,
              },
              '& .MuiInputBase-input': {
                py: 0.5,
              }
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            size="small"
            onClick={handleFilterClick}
            sx={{
              borderRadius: '2px',
              textTransform: 'none',
              py: 0.25,
              px: 1,
              height: 28,
              ml: 'auto',
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Status
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.active}
                  onChange={(e) => handleFilterChange('status', { active: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.inactive}
                  onChange={(e) => handleFilterChange('status', { inactive: e.target.checked })}
                />
              }
              label="Inactive"
            />
          </FormGroup>

          <Typography variant="subtitle1" sx={{ mt: 2 }} gutterBottom>
            Customer Type
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.type.B2B}
                  onChange={(e) => handleFilterChange('type', { B2B: e.target.checked })}
                />
              }
              label="B2B"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.type.B2C}
                  onChange={(e) => handleFilterChange('type', { B2C: e.target.checked })}
                />
              }
              label="B2C"
            />
          </FormGroup>
        </Box>
      </Menu>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '200px' }}>Code</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Name</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Email</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '150px' }}>Street Name</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Phone No</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '150px' }}>VAT No</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Type</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '100px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      No customers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow
                      key={customer._id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="left" sx={{ py: 0.5 }}>{customer.code || '-'}</TableCell>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', py: 0.5 }}>
                        {customer.name || '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{customer.email || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{customer.address?.street || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{customer.phone || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{customer.vatRegistrationNumber || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{customer.customerType || '-'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip
                          label={customer.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={customer.isActive ? 'success' : 'error'}
                          variant="outlined"
                          sx={{
                            fontWeight: 'medium',
                            backgroundColor: !customer.isActive ? 'rgba(211, 47, 47, 0.1)' : 'transparent'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleEditClick(customer)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleDeleteClick(customer)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
              cachedPage = newPage;
            }}
            onRowsPerPageChange={(event) => {
              const newRowsPerPage = parseInt(event.target.value, 10);
              setRowsPerPage(newRowsPerPage);
              cachedRowsPerPage = newRowsPerPage;
              setPage(0);
              cachedPage = 0;
            }}
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Customer"
        message={`Are you sure you want to delete "${selectedCustomer?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* ── Field Mapping Modal ─────────────────────────────────────────── */}
      <Dialog
        open={mappingModalOpen}
        onClose={handleCloseMappingModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            overflow: 'hidden',
            position: 'relative',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
            fontSize: '1rem',
            background: 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          Map Excel Columns to Customer Fields
          {importFile && (
            <Typography
              variant="caption"
              sx={{ ml: 'auto', opacity: 0.85, fontWeight: 400 }}
            >
              {importFile.name} &nbsp;·&nbsp; {parsedRows.length} rows
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, mt: '20px' }}>
          {importLoading && <LinearProgress sx={{ mb: 2 }} />}

          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}

          {importFailedRecords && importFailedRecords.length > 0 && (
            <Box sx={{ mb: 3, mt: 1 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Failed Records Details
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ffcdd2', borderRadius: '8px', bgcolor: '#fff' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600, width: '150px' }}>Row / Status</TableCell>
                      <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Record Data</TableCell>
                      <TableCell sx={{ bgcolor: '#ffebee', fontWeight: 600 }}>Error Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importFailedRecords.map((record, index) => {
                      const rowNum = record.rowNumber || record.row || (index + 1);
                      const errors = record.errors || record.error;

                      const renderErrors = (errs) => {
                        if (!errs) return <Typography variant="caption" color="error">Unknown error</Typography>;
                        if (typeof errs === 'string') return <Typography variant="caption" color="error">{errs}</Typography>;
                        if (Array.isArray(errs)) {
                          return (
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#d32f2f' }}>
                              {errs.map((err, i) => (
                                <li key={i}>
                                  <Typography variant="caption">
                                    {typeof err === 'object' ? (
                                      <><strong>{err.field || 'Error'}:</strong> {err.message || err.error || JSON.stringify(err)}</>
                                    ) : (
                                      err
                                    )}
                                  </Typography>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        return (
                          <ul style={{ margin: 0, paddingLeft: '20px', color: '#d32f2f' }}>
                            {Object.entries(errs).map(([field, msg]) => (
                              <li key={field}>
                                <Typography variant="caption">
                                  <strong>{field}:</strong> {typeof msg === 'string' ? msg : JSON.stringify(msg)}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        );
                      };

                      return (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" fontWeight="bold">
                              Row {rowNum}
                            </Typography>
                            <Chip label="Failed" size="small" color="error" sx={{ mt: 1, height: 20, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {record.data || record.record ? (
                              <Box>
                                {(record.data?.name || record.record?.name) && <Typography variant="caption" display="block"><strong>Name:</strong> {record.data?.name || record.record?.name}</Typography>}
                                {(record.data?.code || record.record?.code) && <Typography variant="caption" display="block"><strong>Code:</strong> {record.data?.code || record.record?.code}</Typography>}
                                {(record.data?.email || record.record?.email) && <Typography variant="caption" display="block"><strong>Email:</strong> {record.data?.email || record.record?.email}</Typography>}
                                {(record.data?.vatRegistrationNumber || record.record?.vatRegistrationNumber) && <Typography variant="caption" display="block"><strong>VAT:</strong> {record.data?.vatRegistrationNumber || record.record?.vatRegistrationNumber}</Typography>}
                              </Box>
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {renderErrors(errors)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {bulkFieldsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {bulkFieldsError}
            </Alert>
          )}

          {bulkFieldsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading API fields…
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                maxHeight: 380,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {bulkFields.map((field) => {
                const apiField =
                  typeof field === 'object'
                    ? field.key || field.name || field.field || JSON.stringify(field)
                    : field;
                const label =
                  typeof field === 'object'
                    ? field.label || field.displayName || apiField
                    : field;
                const required =
                  typeof field === 'object' ? !!field.required : false;

                return (
                  <Box
                    key={apiField}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: '6px',
                      border: '1px solid rgba(0,0,0,0.08)',
                      bgcolor: 'rgba(67,97,238,0.02)',
                    }}
                  >
                    {/* API field name */}
                    <Box sx={{ flex: '0 0 auto', minWidth: 140 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: required ? 'error.main' : 'text.primary',
                          fontSize: '0.78rem',
                        }}
                      >
                        {label}
                        {required && (
                          <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                            *
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {/* Arrow */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.disabled', flex: '0 0 auto' }}
                    >
                      ←
                    </Typography>

                    {/* Excel header selector */}
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={fieldMapping[apiField] || ''}
                        onChange={(e) => handleMappingChange(apiField, e.target.value)}
                        displayEmpty
                        disabled={importLoading}
                        sx={{
                          fontSize: '0.8rem',
                          '& .MuiOutlinedInput-notchedOutline': { borderRadius: '4px' },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: '#999', fontSize: '0.8rem' }}>— not mapped —</em>
                        </MenuItem>
                        {parsedHeaders.map((h) => (
                          <MenuItem key={h} value={h} sx={{ fontSize: '0.82rem' }}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Mini preview */}
          {/* {parsedRows.length > 0 && parsedHeaders.length > 0 && !bulkFieldsLoading && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75 }}
              >
                Preview — first 3 rows of your Excel file
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid rgba(0,0,0,0.10)', borderRadius: '4px', maxHeight: 160 }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {parsedHeaders.map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontWeight: 700,
                            bgcolor: 'rgba(67,97,238,0.07)',
                            py: 0.5,
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.slice(0, 3).map((row, idx) => (
                      <TableRow key={idx}>
                        {parsedHeaders.map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              py: 0.4,
                              fontSize: '0.75rem',
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {String(row[h] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )} */}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1,
            borderTop: '1px solid rgba(0,0,0,0.08)',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseMappingModal}
            disabled={importLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={parsedRows.length === 0 || importLoading || bulkFieldsLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              minWidth: 110,
              background: importLoading
                ? 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)'
                : 'linear-gradient(45deg, #4361ee 0%, #4895ef 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)',
              },
              '&.Mui-disabled': {
                background: importLoading
                  ? 'linear-gradient(45deg, #3b54d6 0%, #3d7dce 100%)'
                  : undefined,
                color: importLoading ? '#fff' : undefined,
                opacity: importLoading ? 0.85 : undefined,
              },
            }}
          >
            {importLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Lottie
                  animationData={loadingSpinnerData}
                  loop
                  autoplay
                  style={{ width: 22, height: 22 }}
                />
                Creating…
              </Box>
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
        {importLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(5px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              animation: 'fadeIn 0.2s ease-in-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 },
              },
            }}
          >
            <Lottie
              animationData={loadingSpinnerData}
              loop
              autoplay
              style={{ width: 90, height: 90, marginBottom: 8 }}
            />
            <Typography variant="h6" color="text.primary" fontWeight={600}>
              Creating records...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Please wait while your Excel data is being imported.
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* ── Success / Warning Toast ─────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '8px', boxShadow: 4 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Customers; 